import { BoardID } from 'BoardID';
import {
  changePatternMaterial,
  changePatternSize,
  createRandomMaterialPatternString,
  MaterialPattern,
  parseMaterialPatternString,
  scalePatternString,
} from 'Pattern';
import { StripsParams } from 'Types';
import {
  BoardDetails,
  BoardType,
  Dimensions2D,
  Direction,
  Material,
  ParamsPanelSelection,
  Renderable,
  RenderableMetadata,
  Settings,
  TwoArray,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import {
  fillRenderablesAt,
  flippedIndex,
  groupBy,
  isFlipped,
  mapMapEntries,
  scaleNumber,
  sumBy,
  withKerfAndBuffer,
} from 'utils/utils';

export default class Strips extends BoardBase<BoardType.Strips> {
  constructor(id: BoardID, name: string, params: StripsParams, units?: Units) {
    super(BoardType.Strips, id, name, params, units);
  }

  static createRandom(
    materials: TwoArray<Material>,
    size: Dimensions2D
  ): StripsParams {
    return {
      rowHeight: 1,
      numRows: size.height,
      pattern: createRandomMaterialPatternString(
        1 / 4,
        2,
        size.width,
        materials
      ),
      thickness: 1,
      flip: true,
    };
  }

  getPattern(): MaterialPattern {
    return parseMaterialPatternString(this.params.pattern);
  }

  getRenderables(): Array<Renderable<BoardType.Strips>> {
    const { flip, numRows, rowHeight } = this.params;

    const pattern = this.getPattern();
    const patternReverse = flip ? [...pattern].reverse() : pattern;

    let renderables: Array<Renderable<BoardType.Strips>> = [];

    for (let rowIndex = 0; rowIndex < numRows; ++rowIndex) {
      const isRowFlipped = isFlipped(rowIndex, flip);
      const adjustedPattern = isRowFlipped ? patternReverse : pattern;

      fillRenderablesAt(
        renderables,
        adjustedPattern,
        rowIndex * rowHeight,
        0,
        rowHeight,
        Direction.H,
        ({ fragmentIndex, renderableIndex }) => ({
          type: BoardType.Strips,
          fragmentIndex: flippedIndex(
            pattern.length,
            fragmentIndex,
            isRowFlipped
          ),
          rowIndex,
          renderableIndex,
        })
      );
    }

    return renderables;
  }

  getIsRenderableHighlighted(
    renderable: Renderable<BoardType.Strips>,
    selection: Renderable<BoardType.Strips>
  ): boolean {
    return (
      renderable.metadata.fragmentIndex === selection.metadata.fragmentIndex
    );
  }

  getBoardDetails(settings: Settings): BoardDetails {
    const { kerf, planeBuffer } = settings;
    const { numRows, rowHeight, thickness } = this.params;
    const pattern = this.getPattern();
    const patternFragmentsByMaterial = groupBy(pattern, 'key');

    // const finalFBMByMaterial = mapValues(
    //   patternFragmentsByMaterial,
    //   dimensions => ({
    //     height: numRows * thickness,
    //     width: sumBy(dimensions, 'size'),
    //     thickness: rowHeight,
    //   }),
    // );

    const startingStockLength = withKerfAndBuffer(thickness, numRows, settings);

    const startingStockThickness = rowHeight + planeBuffer;

    const startingStockByMaterial = mapMapEntries(
      patternFragmentsByMaterial,
      (material, dimensions) => ({
        material,
        dimensions: {
          length: startingStockLength,
          thickness: startingStockThickness,
          width:
            sumBy(dimensions, ({ size }) => size + planeBuffer) +
            (dimensions.length - 1) * kerf,
        },
      })
    );

    return { idealStartingStock: [{ stock: startingStockByMaterial }] };
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<BoardType.Strips>,
    material: Material
  ) {
    return this.updateParams({
      pattern: changePatternMaterial(
        this.params.pattern,
        metadata.fragmentIndex,
        material
      ),
    });
  }

  handleSelectedSizeChange(
    { metadata }: Renderable<BoardType.Strips>,
    size: number
  ) {
    return this.updateParams({
      pattern: changePatternSize(
        this.params.pattern,
        metadata.fragmentIndex,
        size
      ),
    });
  }

  previewSelectionToParamsPanelSelection(
    selection: RenderableMetadata<BoardType.Strips>
  ): ParamsPanelSelection<BoardType.Chevron> {
    return {
      renderableIndex: selection.fragmentIndex,
    };
  }

  paramsPanelSelectionToPreviewSelection(
    selection: ParamsPanelSelection<BoardType.Chevron>
  ): null | RenderableMetadata<BoardType.Strips> {
    return selection == null
      ? null
      : {
          ...selection,
          fragmentIndex: selection.renderableIndex,
          rowIndex: 0,
        };
  }

  scaleParams(params: StripsParams, scaleFactor: number): StripsParams {
    return {
      ...params,
      rowHeight: scaleNumber(params.rowHeight, scaleFactor),
      thickness: scaleNumber(params.thickness, scaleFactor),
      pattern: scalePatternString(params.pattern, scaleFactor),
    };
  }
}
